import React, { useMemo, useState } from "react";
import toast from "react-hot-toast";

import Modal from "../../../components/modal/Modal";
import ConfirmModal from "../../../components/modal/ConfirmModal";
import Button from "../../../components/button/Button";
import IconButton from "../../../components/button/IconButton";
import Input from "../../../components/form-item/Input";
import Select from "../../../components/form-item/Select";
import Toggle from "../../../components/form-item/Toggle";
import HelpHint from "../../../components/help-hint/HelpHint";
import ContentTag from "../../../components/content-tag/ContentTag";
import TagSelector from "../../../components/tag-selector/TagSelector";
import UserListRow from "../../../components/user-item/UserListRow";
import { useSearchAdminProjectsQuery } from "../../../app/services/adminAcr";
import {
  useCreateAdminGitHubRepositoryMutation,
  useDeleteAdminGitHubRepositoryMutation,
  useGetAdminGitHubAvailableRepositoriesQuery,
  useGetAdminGitHubRoleCandidatesQuery,
  useUpdateAdminGitHubRepositoryMutation,
  type AdminGitHubRepository,
  type GitHubPosition,
  type GitHubRoleInput,
  type GitHubUserBrief,
} from "../../../app/services/github";
import { POSITION_OPTIONS } from "../positions";

interface SelectedProject {
  id: string;
  name: string;
}

export interface GitHubRepositorySettingsModalProps {
  /** null なら新規登録 */
  repository: AdminGitHubRepository | null;
  onClose: () => void;
}

const FieldTitle: React.FC<{ title: string; hint?: string }> = ({ title, hint }) => (
  <div className="u-row u-items-center u-gap4">
    <p className="heading-13 u-m0">{title}</p>
    {hint && <HelpHint description={hint} placement="right" size={16} />}
  </div>
);

/**
 * GitHub リポジトリの登録・権限設定モーダル（master_admin 専用）。
 *
 * 共有ライブラリのセクション設定（LibrarySectionSettingsModal）と同じ項目:
 * 対象プロジェクト（1 件以上必須）/ 閲覧できるポジション / アクセス期間 /
 * 書き込みロール（エディター。ポジション紐付け or ユーザー直接指定）。
 */
const GitHubRepositorySettingsModal: React.FC<GitHubRepositorySettingsModalProps> = ({
  repository,
  onClose,
}) => {
  const isNew = repository === null;
  const settings = repository?.settings;

  const [selectedRepoKey, setSelectedRepoKey] = useState("");
  const [projects, setProjects] = useState<SelectedProject[]>(settings?.projects ?? []);
  const [allowedPositions, setAllowedPositions] = useState<GitHubPosition[]>(
    settings?.allowed_positions ?? [],
  );
  const [allowCompleted, setAllowCompleted] = useState(settings?.allow_completed ?? true);
  const [editorPositions, setEditorPositions] = useState<GitHubPosition[]>(
    (settings?.roles ?? []).flatMap((r) => (r.position ? [r.position] : [])),
  );
  const [editorUsers, setEditorUsers] = useState<GitHubUserBrief[]>(
    (settings?.roles ?? []).flatMap((r) => (r.user ? [r.user] : [])),
  );
  const [projectQuery, setProjectQuery] = useState("");
  const [userQuery, setUserQuery] = useState("");
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  const { data: availableData, isFetching: loadingAvailable, isError: availableError } =
    useGetAdminGitHubAvailableRepositoriesQuery(undefined, {
      skip: !isNew,
      // インストール先は GitHub 側でいつでも変わるため、開くたびに取り直す
      refetchOnMountOrArgChange: true,
    });
  const availableOptions = useMemo(
    () =>
      (availableData?.data?.repositories ?? [])
        .filter((r) => !r.registered_repository_id)
        .map((r) => ({
          value: `${r.installation_id}:${r.repo_id}`,
          name: `${r.full_name}${r.is_private ? "（非公開）" : ""}`,
        })),
    [availableData],
  );

  const { data: projectData, isFetching: searchingProjects } = useSearchAdminProjectsQuery(
    { q: projectQuery, limit: 8 },
    { skip: projectQuery.trim() === "" },
  );
  const projectResults = useMemo(() => {
    const selected = new Set(projects.map((p) => p.id));
    return (projectData?.data?.projects ?? []).filter((p) => !selected.has(p.project_id));
  }, [projectData, projects]);

  const { data: candidateData, isFetching: searchingUsers } = useGetAdminGitHubRoleCandidatesQuery(
    { id: repository?.id ?? "", q: userQuery },
    { skip: isNew || userQuery.trim() === "" },
  );
  const candidates = useMemo(() => {
    const assigned = new Set(editorUsers.map((u) => u.id));
    return (candidateData?.data?.users ?? []).filter((u) => !assigned.has(u.id));
  }, [candidateData, editorUsers]);

  const [createRepository, { isLoading: creating }] = useCreateAdminGitHubRepositoryMutation();
  const [updateRepository, { isLoading: updating }] = useUpdateAdminGitHubRepositoryMutation();
  const [deleteRepository, { isLoading: deleting }] = useDeleteAdminGitHubRepositoryMutation();

  const rolesInput = (): GitHubRoleInput[] => [
    ...editorPositions.map((position) => ({ role: "editor" as const, position })),
    ...editorUsers.map((user) => ({ role: "editor" as const, user_id: user.id })),
  ];

  const missingRepository = isNew && !selectedRepoKey;
  const missingProjects = projects.length === 0;

  const handleSave = async () => {
    const body = {
      project_ids: projects.map((p) => p.id),
      allowed_positions: allowedPositions,
      allow_completed: allowCompleted,
      roles: rolesInput(),
    };
    try {
      if (isNew) {
        const [installationId, repoId] = selectedRepoKey.split(":").map(Number);
        await createRepository({ ...body, installation_id: installationId, repo_id: repoId }).unwrap();
        toast.success("リポジトリを登録しました");
      } else {
        await updateRepository({ ...body, id: repository.id }).unwrap();
        toast.success("設定を保存しました");
      }
      onClose();
    } catch (err) {
      const message = (err as { data?: { message?: string } })?.data?.message;
      toast.error(message || "保存に失敗しました");
    }
  };

  const handleDelete = async () => {
    if (!repository) return;
    try {
      await deleteRepository(repository.id).unwrap();
      toast.success("登録を解除しました");
      onClose();
    } catch {
      toast.error("登録の解除に失敗しました");
    }
  };

  if (confirmingDelete && repository) {
    return (
      <ConfirmModal
        message={
          `「${repository.full_name}」の登録を解除しますか？\n` +
          "soremon から操作できなくなります（GitHub 上のリポジトリは変わりません）。"
        }
        confirmLabel="解除"
        loading={deleting}
        onConfirm={handleDelete}
        onCancel={() => setConfirmingDelete(false)}
      />
    );
  }

  return (
    <Modal
      title={isNew ? "リポジトリを登録" : repository.full_name}
      titleSize="medium"
      size="default"
      onClose={onClose}
      primaryAction={{
        label: isNew ? "登録" : "保存",
        onClick: handleSave,
        loading: creating || updating,
        disabled: missingRepository || missingProjects,
      }}
      secondaryAction={{ label: "キャンセル", onClick: onClose }}
    >
      <div className="u-col u-gap16 u-w100">
        {isNew && (
          <div className="u-col u-gap8 u-w100">
            <FieldTitle
              title="リポジトリ"
              hint="GitHub App（soremon-bot）をインストール済みのリポジトリから選びます。一覧に無い場合は App のインストール先にリポジトリを追加してください。"
            />
            {availableError ? (
              <p className="bodytext-13 u-text-light-gray u-m0">
                リポジトリ一覧を取得できませんでした（GitHub App の設定を確認してください）
              </p>
            ) : (
              <Select
                value={selectedRepoKey}
                placeholder={loadingAvailable ? "読み込み中..." : "選択してください"}
                options={availableOptions}
                onChange={(e) => setSelectedRepoKey(e.target.value)}
                disabled={loadingAvailable}
              />
            )}
          </div>
        )}

        <div className="u-col u-gap8 u-w100">
          <FieldTitle
            title="対象プロジェクト"
            hint="このリポジトリを使うプロジェクトです（1 つ以上）。閲覧できるのは対象プロジェクトのメンバーです。"
          />
          {projects.length > 0 && (
            <div className="u-row u-wrap u-gap4">
              {projects.map((p) => (
                <ContentTag
                  key={p.id}
                  removeLabel={`${p.name} を外す`}
                  onRemove={() => setProjects((prev) => prev.filter((sp) => sp.id !== p.id))}
                >
                  {p.name}
                </ContentTag>
              ))}
            </div>
          )}
          <Input
            type="search"
            value={projectQuery}
            onChange={(e) => setProjectQuery(e.target.value)}
            placeholder="プロジェクト名で検索..."
          />
          {searchingProjects && <p className="bodytext-11 u-text-light-gray u-m0">検索中...</p>}
          {projectResults.map((p) => (
            <Button
              key={p.project_id}
              size="small"
              theme="secondary"
              variant="text"
              leftIcon="add"
              onClick={() => {
                setProjects((prev) => [...prev, { id: p.project_id, name: p.name }]);
                setProjectQuery("");
              }}
            >
              {p.name}
            </Button>
          ))}
        </div>

        <div className="u-col u-gap8 u-w100">
          <FieldTitle
            title="閲覧できるポジション"
            hint="未選択の場合は対象プロジェクトの全メンバーが閲覧できます。プロジェクト内の管理者は常に閲覧・書き込みできます。"
          />
          <TagSelector tags={POSITION_OPTIONS} selectedIds={allowedPositions} onChange={setAllowedPositions} />
        </div>

        <div className="u-col u-gap8 u-w100">
          <FieldTitle title="アクセス期間" />
          <Toggle value={allowCompleted} onClick={() => setAllowCompleted((v) => !v)}>
            <span className="bodytext-13">
              {allowCompleted ? "プロジェクト完了後もアクセス可" : "プロジェクト進行中のみアクセス可"}
            </span>
          </Toggle>
        </div>

        <div className="u-col u-gap8 u-w100">
          <FieldTitle
            title="書き込みできるメンバー"
            hint="soremon からブランチ作成・コミット・PR 作成ができるメンバーです。ポジションかユーザーで指定します。デフォルトブランチへの直接コミットは誰もできません。"
          />
          <TagSelector tags={POSITION_OPTIONS} selectedIds={editorPositions} onChange={setEditorPositions} />
          {editorUsers.length > 0 && (
            <div className="u-row u-wrap u-gap4">
              {editorUsers.map((user) => (
                <ContentTag
                  key={user.id}
                  removeLabel={`${user.display_name} を外す`}
                  onRemove={() => setEditorUsers((prev) => prev.filter((u) => u.id !== user.id))}
                >
                  {user.display_name}
                </ContentTag>
              ))}
            </div>
          )}
          {isNew ? (
            <p className="bodytext-11 u-text-light-gray u-m0">ユーザーの直接指定は登録後に設定できます</p>
          ) : (
            <>
              <Input
                type="search"
                value={userQuery}
                onChange={(e) => setUserQuery(e.target.value)}
                placeholder="表示名 / ユーザー名 / メールで検索..."
              />
              {searchingUsers && <p className="bodytext-11 u-text-light-gray u-m0">検索中...</p>}
              {candidates.map((user) => (
                <UserListRow
                  key={user.id}
                  variant="flat"
                  avatarSize={32}
                  user={{ id: user.id, display_name: user.display_name, avatar_url: user.avatar }}
                  metaSlot={
                    user.positions.length > 0 ? (
                      <span className="bodytext-11 u-text-light-gray">{user.positions.join(" / ")}</span>
                    ) : undefined
                  }
                  trailingSlot={
                    <IconButton
                      icon="add"
                      size={20}
                      ariaLabel={`${user.display_name} を追加`}
                      onClick={() => {
                        setEditorUsers((prev) => [...prev, user]);
                        setUserQuery("");
                      }}
                    />
                  }
                />
              ))}
            </>
          )}
        </div>

        {!isNew && (
          <div className="u-row-end u-w100">
            <Button
              size="small"
              theme="secondary"
              variant="text"
              leftIcon="link_off"
              onClick={() => setConfirmingDelete(true)}
            >
              登録を解除
            </Button>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default GitHubRepositorySettingsModal;
